/**
 * `/api/boss-matrix`, `/api/boss-plan/**`, `/api/boss-clear/**` and `/api/income` — the weekly
 * character x boss matrix and its crystal-income totals. See `BossDtos` / `BossController` /
 * `BossService` for the authoritative shapes (WP4 D15: a clear's difficulty always comes from the
 * character's `boss_plan`, never from the clear request itself — the UI sets a plan before it can
 * record a clear).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend, ApiError } from './api';
import { pushToast } from './events';

export type BossTierValue = 'DAILY' | 'WEEKLY' | 'MONTHLY';

export interface BossPlanDto {
  characterId: number;
  bossSlug: string;
  bossName: string;
  tier: BossTierValue;
  sharedClearGroup: string;
  difficulty: string;
  partySize: number;
  levelReq: number | null;
  forceType: string | null;
  forceReq: number | null;
  partyMax: number | null;
  crystalValue: number | null;
  crystalType: string | null;
  crystalVerified: boolean;
}

export interface BossClearDto {
  characterId: number;
  bossSlug: string;
  bossName: string;
  tier: BossTierValue;
  sharedClearGroup: string;
  difficulty: string;
  periodKey: string;
  crystalSold: boolean;
  clearedAt: string;
  crystalValue: number | null;
  crystalType: string | null;
  /** REV5: Gollux Coins recorded for this clear. `null` = not recorded. Never mesos/crystal income. */
  coinsEarned: number | null;
}

export interface BossMatrixCharacter {
  characterId: number;
  name: string;
  classSlug: string;
  level: number;
  arcaneForce: number | null;
  sacredForce: number | null;
  plans: BossPlanDto[];
  clears: BossClearDto[];
  /** Boss slugs this character has hidden from its own matrix view (display-only). */
  hiddenBossSlugs: string[];
}

export interface BossMatrixResponse {
  accountId: number;
  worldSlug: string;
  weekKey: string;
  weekEnd: string;
  monthlyKeys: string[];
  nextWeeklyReset: string;
  characters: BossMatrixCharacter[];
}

export interface IncomeCharacter {
  characterId: number;
  name: string;
  totalMesos: number;
  soldClears: number;
  unpricedClears: number;
  weeklyCrystalsSold: number;
  characterCap: number;
  characterCapReached: boolean;
}

export interface IncomeResponse {
  accountId: number;
  worldSlug: string;
  weekKey: string;
  totalMesos: number;
  /** The slice of totalMesos from MONTHLY-tier bosses (Black Mage) — always ≤ totalMesos. */
  monthlyMesos: number;
  soldClears: number;
  unpricedClears: number;
  worldWeeklyCrystalsSold: number;
  worldCap: number;
  worldCapReached: boolean;
  characters: IncomeCharacter[];
}

export function bossMatrixQuery(accountId: number, weekKey: string) {
  return {
    queryKey: ['boss-matrix', accountId, weekKey] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet<BossMatrixResponse>(`/api/boss-matrix?accountId=${accountId}&week=${weekKey}`, signal),
  };
}

export function incomeQuery(accountId: number, weekKey: string) {
  return {
    queryKey: ['income', accountId, weekKey] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet<IncomeResponse>(`/api/income?accountId=${accountId}&week=${weekKey}`, signal),
  };
}

export function useBossMatrix(accountId: number | null, weekKey: string) {
  return useQuery({
    ...bossMatrixQuery(accountId ?? -1, weekKey),
    enabled: accountId !== null,
  });
}

export function useIncome(accountId: number | null, weekKey: string) {
  return useQuery({
    ...incomeQuery(accountId ?? -1, weekKey),
    enabled: accountId !== null,
  });
}

function invalidateBossState(
  queryClient: ReturnType<typeof useQueryClient>,
  accountId: number,
  weekKey: string,
) {
  void queryClient.invalidateQueries({ queryKey: ['boss-matrix', accountId, weekKey] });
  void queryClient.invalidateQueries({ queryKey: ['income', accountId, weekKey] });
}

/** Deep-replaces one character's plan for one boss in a cached matrix response. */
function withPlanUpserted(matrix: BossMatrixResponse, plan: BossPlanDto): BossMatrixResponse {
  return {
    ...matrix,
    characters: matrix.characters.map((character) =>
      character.characterId !== plan.characterId
        ? character
        : {
            ...character,
            plans: [...character.plans.filter((p) => p.bossSlug !== plan.bossSlug), plan],
          },
    ),
  };
}

/** Deep-replaces (or removes) one character's clear for one boss+period in a cached matrix response. */
function withClearUpserted(
  matrix: BossMatrixResponse,
  characterId: number,
  bossSlug: string,
  periodKey: string,
  clear: BossClearDto | null,
): BossMatrixResponse {
  return {
    ...matrix,
    characters: matrix.characters.map((character) => {
      if (character.characterId !== characterId) {
        return character;
      }
      const withoutThisOne = character.clears.filter(
        (c) => !(c.bossSlug === bossSlug && c.periodKey === periodKey),
      );
      return { ...character, clears: clear ? [...withoutThisOne, clear] : withoutThisOne };
    }),
  };
}

export interface UpsertPlanVariables {
  accountId: number;
  weekKey: string;
  characterId: number;
  bossSlug: string;
  bossName: string;
  tier: BossTierValue;
  sharedClearGroup: string;
  difficulty: string;
  partySize: number;
  levelReq: number | null;
  forceType: string | null;
  forceReq: number | null;
  partyMax: number | null;
  crystalValue: number | null;
  crystalType: string | null;
  crystalVerified: boolean;
}

/**
 * `PUT /api/boss-plan/{characterId}/{bossSlug}` — sets the difficulty a character intends to run.
 * Optimistic apply + rollback-on-error, same shape as WP5's `useCompleteTask`. The optimistic entry
 * is built from the boss-catalog fields the caller already has (from `useBossCatalog`), so the very
 * first plan for a boss (no prior row to swap) still updates the cell instantly.
 */
export function useUpsertBossPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: UpsertPlanVariables) =>
      apiSend<BossPlanDto>('PUT', `/api/boss-plan/${vars.characterId}/${vars.bossSlug}`, {
        difficulty: vars.difficulty,
        partySize: vars.partySize,
      }),
    onMutate: async (vars) => {
      const key = bossMatrixQuery(vars.accountId, vars.weekKey).queryKey;
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BossMatrixResponse>(key);
      if (previous) {
        const plan: BossPlanDto = {
          characterId: vars.characterId,
          bossSlug: vars.bossSlug,
          bossName: vars.bossName,
          tier: vars.tier,
          sharedClearGroup: vars.sharedClearGroup,
          difficulty: vars.difficulty,
          partySize: vars.partySize,
          levelReq: vars.levelReq,
          forceType: vars.forceType,
          forceReq: vars.forceReq,
          partyMax: vars.partyMax,
          crystalValue: vars.crystalValue,
          crystalType: vars.crystalType,
          crystalVerified: vars.crystalVerified,
        };
        queryClient.setQueryData<BossMatrixResponse>(key, withPlanUpserted(previous, plan));
      }
      return { previous, key };
    },
    onError: (error, _vars, context) => {
      if (context) {
        queryClient.setQueryData(context.key, context.previous);
      }
      const message = error instanceof ApiError ? error.serverMessage : 'Could not save that plan.';
      pushToast(`${message} Your change was rolled back — try again.`);
    },
    onSettled: (_data, _error, vars) => invalidateBossState(queryClient, vars.accountId, vars.weekKey),
  });
}

export interface DeleteBossPlanVariables {
  accountId: number;
  weekKey: string;
  characterId: number;
  bossSlug: string;
}

/** Removes one character's plan for one boss from a cached matrix response (its clears untouched). */
function withPlanRemoved(matrix: BossMatrixResponse, characterId: number, bossSlug: string): BossMatrixResponse {
  return {
    ...matrix,
    characters: matrix.characters.map((character) =>
      character.characterId !== characterId
        ? character
        : { ...character, plans: character.plans.filter((p) => p.bossSlug !== bossSlug) },
    ),
  };
}

/**
 * `DELETE /api/boss-plan/{characterId}/{bossSlug}` — removes a boss from a character's list (the
 * "character tab" redesign's core primitive: a character's boss list *is* its `boss_plan` rows).
 * Idempotent server-side; optimistic apply + rollback-on-error, same shape as the other boss
 * mutations. Any recorded clear for this boss is left alone — clears history is independent of the
 * plan that produced it (backend addendum, see `docs/specs/POST-MVP-fixes.md` §8).
 */
export function useDeleteBossPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: DeleteBossPlanVariables) =>
      apiSend<void>('DELETE', `/api/boss-plan/${vars.characterId}/${vars.bossSlug}`),
    onMutate: async (vars) => {
      const key = bossMatrixQuery(vars.accountId, vars.weekKey).queryKey;
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BossMatrixResponse>(key);
      if (previous) {
        queryClient.setQueryData<BossMatrixResponse>(
          key,
          withPlanRemoved(previous, vars.characterId, vars.bossSlug),
        );
      }
      return { previous, key };
    },
    onError: (error, _vars, context) => {
      if (context) {
        queryClient.setQueryData(context.key, context.previous);
      }
      const message = error instanceof ApiError ? error.serverMessage : 'Could not remove that boss.';
      pushToast(`${message} Your change was rolled back — try again.`);
    },
    onSettled: (_data, _error, vars) => invalidateBossState(queryClient, vars.accountId, vars.weekKey),
  });
}

export interface UpsertClearVariables {
  accountId: number;
  weekKey: string;
  characterId: number;
  bossSlug: string;
  bossName: string;
  tier: BossTierValue;
  sharedClearGroup: string;
  /** The character's current planned difficulty — a clear always inherits it (WP4 D15). */
  difficulty: string;
  crystalValue: number | null;
  crystalType: string | null;
  /** The cadence-appropriate key for the period being written (today/this week/this month). */
  periodKey: string;
  cleared: boolean;
  crystalSold: boolean;
  /**
   * REV5: optional Gollux Coins for this clear. `undefined` (the default for every call site except
   * the coin input's own commit handler) means "leave whatever is already recorded" — it is dropped
   * from the request body entirely (`JSON.stringify` skips `undefined` values), which the backend's
   * `COALESCE(?, coins_earned)` treats the same way a missing key would.
   */
  coinsEarned?: number;
}

/**
 * `PUT /api/boss-clear/{characterId}/{bossSlug}` — records or removes a clear. Optimistic apply +
 * rollback-on-error; a 409 (two difficulties of the same shared-clear-group cleared in one period)
 * rolls back and surfaces the server's own explanation, which already names both clears.
 */
export function useUpsertBossClear() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: UpsertClearVariables) =>
      apiSend('PUT', `/api/boss-clear/${vars.characterId}/${vars.bossSlug}`, {
        periodKey: vars.periodKey,
        cleared: vars.cleared,
        crystalSold: vars.crystalSold,
        coinsEarned: vars.coinsEarned,
      }),
    onMutate: async (vars) => {
      const key = bossMatrixQuery(vars.accountId, vars.weekKey).queryKey;
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BossMatrixResponse>(key);
      if (previous) {
        const previousClear = previous.characters
          .find((c) => c.characterId === vars.characterId)
          ?.clears.find((c) => c.bossSlug === vars.bossSlug && c.periodKey === vars.periodKey);
        const optimisticClear: BossClearDto | null = vars.cleared
          ? {
              characterId: vars.characterId,
              bossSlug: vars.bossSlug,
              bossName: vars.bossName,
              tier: vars.tier,
              sharedClearGroup: vars.sharedClearGroup,
              difficulty: vars.difficulty,
              periodKey: vars.periodKey,
              crystalSold: vars.crystalSold,
              clearedAt: new Date().toISOString(),
              crystalValue: vars.crystalValue,
              crystalType: vars.crystalType,
              // Same "leave it alone when absent" semantics as the request itself (see
              // UpsertClearVariables.coinsEarned) — a crystalSold-only optimistic update must not
              // flash the coin input back to empty before the server's response lands.
              coinsEarned: vars.coinsEarned ?? previousClear?.coinsEarned ?? null,
            }
          : null;
        queryClient.setQueryData<BossMatrixResponse>(
          key,
          withClearUpserted(previous, vars.characterId, vars.bossSlug, vars.periodKey, optimisticClear),
        );
      }
      return { previous, key };
    },
    onError: (error, _vars, context) => {
      if (context) {
        queryClient.setQueryData(context.key, context.previous);
      }
      const message =
        error instanceof ApiError ? error.serverMessage : 'Could not save that clear.';
      pushToast(`${message} Your change was rolled back — try again.`);
    },
    onSettled: (_data, _error, vars) => invalidateBossState(queryClient, vars.accountId, vars.weekKey),
  });
}

// -------------------------------------------------------------------------------------------
// Hidden bosses (display-only per-character preference — post-MVP)
// -------------------------------------------------------------------------------------------

export interface ToggleHiddenBossVariables {
  accountId: number;
  characterId: number;
  bossSlug: string;
}

/** Deep-replaces one character's `hiddenBossSlugs` in a cached matrix response. */
function withHiddenBossSet(
  matrix: BossMatrixResponse,
  characterId: number,
  bossSlug: string,
  hidden: boolean,
): BossMatrixResponse {
  return {
    ...matrix,
    characters: matrix.characters.map((character) => {
      if (character.characterId !== characterId) {
        return character;
      }
      const withoutThisOne = character.hiddenBossSlugs.filter((slug) => slug !== bossSlug);
      return {
        ...character,
        hiddenBossSlugs: hidden ? [...withoutThisOne, bossSlug] : withoutThisOne,
      };
    }),
  };
}

/**
 * `hiddenBossSlugs` is not period-scoped (unlike plans/clears), so a toggle has to reach into
 * *every* cached `boss-matrix` query for this account, not just the one the caller happens to be
 * viewing — otherwise switching weeks after hiding a boss elsewhere would show it again from a
 * stale cache entry. `['boss-matrix', accountId]` is a prefix of the full `['boss-matrix',
 * accountId, weekKey]` key, so `setQueriesData`/`getQueriesData`/`invalidateQueries` all match
 * every cached week for the account at once (TanStack Query's normal partial-key matching).
 */
function useToggleHiddenBoss(method: 'PUT' | 'DELETE', hidden: boolean, errorFallback: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: ToggleHiddenBossVariables) =>
      apiSend<void>(method, `/api/characters/${vars.characterId}/hidden-bosses/${vars.bossSlug}`),
    onMutate: async (vars) => {
      const prefix = ['boss-matrix', vars.accountId] as const;
      await queryClient.cancelQueries({ queryKey: prefix });
      const previous = queryClient.getQueriesData<BossMatrixResponse>({ queryKey: prefix });
      queryClient.setQueriesData<BossMatrixResponse>({ queryKey: prefix }, (old) =>
        old ? withHiddenBossSet(old, vars.characterId, vars.bossSlug, hidden) : old,
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      context?.previous.forEach(([key, data]) => queryClient.setQueryData(key, data));
      const message = error instanceof ApiError ? error.serverMessage : errorFallback;
      pushToast(`${message} Your change was rolled back — try again.`);
    },
    onSettled: (_data, _error, vars) =>
      void queryClient.invalidateQueries({ queryKey: ['boss-matrix', vars.accountId] }),
  });
}

/** `PUT /api/characters/{id}/hidden-bosses/{bossSlug}` — optimistic apply + rollback on error. */
export function useHideBoss() {
  return useToggleHiddenBoss('PUT', true, 'Could not hide that boss.');
}

/** `DELETE /api/characters/{id}/hidden-bosses/{bossSlug}` — optimistic apply + rollback on error. */
export function useUnhideBoss() {
  return useToggleHiddenBoss('DELETE', false, 'Could not unhide that boss.');
}
